'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'

/**
 * Reading-progress bar pinned to the top of the viewport.
 *
 * Started life inside the Lakbay AI case study and was lifted out so every
 * marketing page shares it. Paints in `--cta` (the brand magenta) and sits at
 * z-50 so it clears the sticky header at z-40.
 *
 * No track behind the fill on purpose: the case study could afford one because
 * it opened on a black hero, but a translucent-white rail would read as a grey
 * hairline across every light page.
 *
 * Mounted inside `ConditionalMarketingShell`, so `/platform/app` never renders
 * it. `/admin` is excluded here instead — it nests under the root layout but
 * ships its own dashboard chrome.
 */

/**
 * A page needs at least this much scrollable distance, as a fraction of the
 * viewport, before the bar tracks anything. Below it the bar would fill within
 * a flick of the wheel and read as broken rather than as progress.
 */
const MIN_SCROLL_RATIO = 0.5

export default function ScrollProgress() {
  const pathname = usePathname()
  const fillRef = useRef<HTMLDivElement>(null)
  const isAdmin = pathname?.startsWith('/admin') ?? false

  useEffect(() => {
    const fill = fillRef.current
    if (!fill) return

    let frame = 0

    const paint = () => {
      frame = 0
      const max = document.documentElement.scrollHeight - window.innerHeight
      const floor = window.innerHeight * MIN_SCROLL_RATIO
      const ratio = max > floor ? Math.min(1, window.scrollY / max) : 0
      fill.style.transform = `scaleX(${ratio})`
    }

    // Coalesce to one write per frame. `paint` only reads scroll offsets and
    // writes a transform, so the bar never forces a layout pass.
    const schedule = () => {
      if (frame) return
      frame = requestAnimationFrame(paint)
    }

    paint()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)

    return () => {
      if (frame) cancelAnimationFrame(frame)
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
    // Re-measures on navigation: the next page has its own height, and its
    // restored scroll offset needs a fresh paint.
  }, [pathname])

  if (isAdmin) return null

  return (
    <div
      className='pointer-events-none fixed inset-x-0 top-0 z-50 h-[3px]'
      aria-hidden='true'
    >
      <div
        ref={fillRef}
        className='h-full w-full origin-left bg-cta'
        style={{ transform: 'scaleX(0)' }}
      />
    </div>
  )
}
